// Small pieces every screen uses.
import type { InputHTMLAttributes, ReactNode } from "react";

/** A placeholder while data loads. Lines of the given widths, pulsing softly. */
export function Skeleton({ lines = [60, 80, 40] }: { lines?: number[] }) {
  return (
    <div aria-hidden="true" className="flex flex-col gap-2.5">
      {lines.map((w, i) => (
        <div
          // biome-ignore lint/suspicious/noArrayIndexKey: lines have no identity
          key={i}
          className="h-3 animate-pulse rounded-[4px] bg-hover"
          style={{ width: `${w}%` }}
        />
      ))}
    </div>
  );
}

const AVATAR_GROUNDS = [
  "var(--color-avatar)",
  "var(--color-avatar-2)",
  "var(--color-avatar-3)",
  "var(--color-avatar-4)",
  "var(--color-avatar-5)",
];

/** One of the five pale grounds, always the same for the same seed. */
export function avatarGround(seed: string): string {
  return AVATAR_GROUNDS[avatarGroundIndex(seed) - 1] as string;
}

/** Which of the five grounds (1-5) `seed` takes (for a host drawing it). */
export function avatarGroundIndex(seed: string): number {
  let hash = 0;
  for (let i = 0; i < seed.length; i++)
    hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
  return (hash % AVATAR_GROUNDS.length) + 1;
}

/**
 * A picture, or the first letter on a pale ground picked from `seed` (the
 * person's id where there is one; the name by default), so people tell apart
 * at a glance and keep their color across screens.
 */
export function Avatar({
  name,
  image,
  size = 24,
  seed,
}: {
  name: string;
  image?: string | null;
  size?: number;
  seed?: string;
}) {
  if (image)
    return (
      <img
        src={image}
        alt=""
        width={size}
        height={size}
        className="flex-none rounded-full object-cover"
      />
    );
  return (
    <span
      aria-hidden="true"
      className="inline-flex flex-none items-center justify-center rounded-full font-semibold text-avatar-fg"
      style={{
        width: size,
        height: size,
        fontSize: Math.round(size * 0.44),
        background: avatarGround(seed ?? name),
      }}
    >
      {[...name.trim()][0]?.toUpperCase() ?? "?"}
    </span>
  );
}

/**
 * A segmented control: one thin frame with round ends holding the options side
 * by side, the chosen one tinted inside it (theme, language, a model's effort). The same
 * shape everywhere a single choice is made at once (the owner, 2026-10-30:
 * switch buttons are one container, not loose words). When the options do not
 * fit the width, the track scrolls sideways instead of wrapping into a second
 * row, so it stays one container: the edge with more beyond it fades (the
 * scroll boxes' edge fade) and the chosen one is scrolled into view. 26 px
 * high like the other pills; 32 px on a touch screen, the size of the phone's
 * pills for a finger.
 */
export function Choices<T extends string>({
  label,
  value,
  options,
  onChange,
  disabled,
}: {
  label: string;
  value: T;
  /** `disabled` on one option: shown faint and not chosen. */
  options: ReadonlyArray<{ value: T; label: string; disabled?: boolean }>;
  onChange(value: T): void;
  disabled?: boolean;
}) {
  return (
    <div
      role="radiogroup"
      aria-label={label}
      className="box-border inline-flex h-[26px] w-fit min-w-0 max-w-full gap-0.5 overflow-x-auto rounded-[13px] pointer-coarse:h-[32px] pointer-coarse:rounded-[16px] border border-border p-[2px] align-middle [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
    >
      {options.map((o) => {
        const on = o.value === value;
        return (
          // biome-ignore lint/a11y/useSemanticElements: segments of one control, announced as a radio group
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={on}
            ref={on ? revealChosen : undefined}
            disabled={disabled || o.disabled}
            onClick={() => onChange(o.value)}
            className={`h-full flex-none cursor-pointer whitespace-nowrap rounded-[11px] px-2.5 pointer-coarse:rounded-[14px] pointer-coarse:px-3 text-[12px] disabled:cursor-not-allowed ${
              o.disabled && !on ? "opacity-50" : ""
            } ${
              on
                ? "bg-chosen text-chosen-fg"
                : "text-muted hover:bg-hover hover:text-body"
            }`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Scrolls a segmented control's track so its chosen segment is in view, when
 * the track is narrower than its options. Module-level so that React calls it
 * only when another segment becomes the chosen one, not on every render.
 */
function revealChosen(el: HTMLButtonElement | null) {
  const track = el?.parentElement;
  if (!el || !track || track.scrollWidth <= track.clientWidth) return;
  const box = track.getBoundingClientRect();
  const seg = el.getBoundingClientRect();
  if (seg.left >= box.left && seg.right <= box.right) return;
  track.scrollLeft += seg.left - box.left - (box.width - seg.width) / 2;
}

/**
 * The look of a filter chip over a list (all / archived, unread, a role): a
 * framed pill, 26 px high and fully round, both sides framed so the unchosen
 * ones still read as chips. The chosen one is tinted. The corners are a full
 * circle: with less they looked sharper than the other pills (2026-09-21).
 * A menu that narrows the list (a Space) takes the same look.
 */
export function chipClass(on: boolean): string {
  return `box-border inline-flex h-[26px] flex-none cursor-pointer items-center gap-1 whitespace-nowrap rounded-[13px] border px-[10px] pb-px text-[11.5px] disabled:cursor-not-allowed ${
    on
      ? "border-chosen-border bg-chosen text-chosen-fg"
      : "border-border bg-surface text-muted hover:text-body"
  }`;
}

/** One filter chip that is turned on or off by itself (unread only). */
export function Chip({
  on,
  onClick,
  children,
}: {
  on: boolean;
  onClick(): void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={on}
      onClick={onClick}
      className={chipClass(on)}
    >
      {children}
    </button>
  );
}

/** Filter chips of which exactly one is chosen (all / archived). */
export function ChipChoices<T extends string | null>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: ReadonlyArray<{ value: T; label: string }>;
  onChange(value: T): void;
}) {
  return (
    <div
      role="radiogroup"
      aria-label={label}
      className="flex flex-wrap gap-1.5"
    >
      {options.map((o) => {
        const on = o.value === value;
        return (
          // biome-ignore lint/a11y/useSemanticElements: filter chips, announced as a radio group
          <button
            key={o.value ?? ""}
            type="button"
            role="radio"
            aria-checked={on}
            onClick={() => onChange(o.value)}
            className={chipClass(on)}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

/**
 * An on/off switch (a track and a knob). `label` is read aloud only; the row
 * beside it says what it turns on. The knob moves; nothing changes size.
 */
export function Switch({
  label,
  checked,
  onChange,
  disabled,
}: {
  label: string;
  checked: boolean;
  onChange(next: boolean): void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative inline-block h-[18px] w-[30px] flex-none cursor-pointer rounded-full transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
        checked ? "bg-accent" : "bg-field"
      }`}
    >
      <span
        aria-hidden="true"
        className={`absolute top-[2px] h-[14px] w-[14px] rounded-full bg-surface transition-[left] ${
          checked ? "left-[14px]" : "left-[2px]"
        }`}
      />
    </button>
  );
}

/** A framed one-line input for a row of a settings box (not for text written in place). */
export const ROW_INPUT =
  "plain-field w-full rounded-[7px] border border-field bg-surface px-[10px] py-[6px] text-[13px] text-fg outline-none focus:border-field-strong";

export function RowInput(props: InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input {...props} className={`${ROW_INPUT} ${props.className ?? ""}`} />
  );
}

/** One line of failure, right under what was pressed. */
export function ErrorLine({ children }: { children: ReactNode }) {
  return (
    <p role="alert" className="mt-1 text-[11.5px] text-danger">
      {children}
    </p>
  );
}
